import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useAttributes } from '@/hooks/useAttributes';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { Charges } from '@/services/charges/charges.types';
import { useSearch } from '@tanstack/react-router';
import React from 'react';
import useToggleChargeStatus from '../hooks/useToggleChargeStatus';
import ManageChargeCategoryForm from '../ManageChargeCategory';
import { getDefaultChargeType } from '../utils';
import { getColumns } from './Column';
import { getTitleByActiveTab, TabType } from './helper';

export function ChargesTable({
  data,
  isLoading = false,
  pagination,
}: {
  data: Charges[];
  isLoading: boolean;
  pagination: Pagination;
}) {
  const { onOpen } = useDialogFormStore();
  const { openConfirmation } = useConfirmationStore();
  const { activeTab, agentType } = useSearch({ from: '/(app)/configuration-management/charges/' });
  const canAddNew = useCan({ allOf: [PERMS.charge.manage] });
  const { mutateAsync } = useToggleChargeStatus(activeTab);
  const { data: chargeType = [] } = useAttributes('chargeUserType') || {};
  const defaultChargeType = getDefaultChargeType(activeTab, chargeType);

  const { t } = useDynamicLocale();

  const columns = React.useMemo(
    () => getColumns(onOpen, activeTab, agentType, mutateAsync, t, openConfirmation, defaultChargeType),
    [onOpen, activeTab, agentType, mutateAsync, t, openConfirmation, defaultChargeType],
  );
  const title = getTitleByActiveTab(activeTab);
  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );
  const onAddnew = (activeTab: TabType) => {
    onOpen({
      title: 'Manage Commission',
      okText: t('common.page.modal.submit'),
      size: 'lg',
      modalTitle: 'configurationManagement.charges.assignChargeModal.manageCharge',
      component: <ManageChargeCategoryForm activeTab={activeTab} />,
    });
  };

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    meta: {
      title: t(title),
      actionKey: canAddNew ? 'onAddNew' : '',
      action: canAddNew ? () => onAddnew(activeTab) : null,
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.id),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable className="rounded-t-none" table={table}>
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
